import type { LanguageBundles, TranslationRow, TranslationSource } from './interfaces';

/** The namespace the host application keeps its own texts in. */
export const HOST_NAMESPACE = 'translation';

/** The namespace Enso registers its own texts in. */
export const ENSO_NAMESPACE = 'enso';

/** The languages the screen shows a column for. */
export const LANGUAGES = ['en', 'nl'] as const;

export type Language = (typeof LANGUAGES)[number];

/** A bundle is a tree; the table is a list of the dotted path of each string and its value. */
export const flatten = (node: unknown, prefix = '', out: Map<string, string> = new Map()): Map<string, string> => {
  if (typeof node === 'string') {
    out.set(prefix, node);
  } else if (node && typeof node === 'object') {
    Object.entries(node).forEach(([key, child]) => flatten(child, prefix ? `${prefix}.${key}` : key, out));
  }
  return out;
};

const sourceOf = (inHost: boolean, inEnso: boolean): TranslationSource => {
  if (!inEnso) return 'app';
  return inHost ? 'override' : 'enso';
};

/**
 * One row per key, with the value each language shows: the application's own value when it has one,
 * Enso's otherwise. A key Enso has and the application sets in any language is an override.
 */
export const translationRows = (bundles: Record<Language, LanguageBundles>): TranslationRow[] => {
  const host = { en: flatten(bundles.en.host), nl: flatten(bundles.nl.host) };
  const enso = { en: flatten(bundles.en.enso), nl: flatten(bundles.nl.enso) };
  const keys = new Set([...host.en.keys(), ...host.nl.keys(), ...enso.en.keys(), ...enso.nl.keys()]);

  return [...keys]
    .map(key => {
      const inHost = host.en.has(key) || host.nl.has(key);
      const inEnso = enso.en.has(key) || enso.nl.has(key);
      return {
        key,
        area: key.split('.')[0],
        source: sourceOf(inHost, inEnso),
        en: host.en.get(key) ?? enso.en.get(key) ?? '',
        nl: host.nl.get(key) ?? enso.nl.get(key) ?? '',
      } satisfies TranslationRow;
    })
    .sort((a, b) => a.key.localeCompare(b.key));
};
